import { useState } from "react";
import { toast } from "sonner";
import { Archive, ArrowCounterClockwise, Check, Minus, X } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";

export function useBulkSelection() {
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (ids, on) => setSelected(prev => {
    const next = new Set(prev);
    (Array.isArray(ids) ? ids : [ids]).forEach(id => (on ? next.add(id) : next.delete(id)));
    return next;
  });
  const clear = () => setSelected(new Set());
  return { selected, toggle, clear };
}

// Square select toggle, styled differently from native "complete" checkboxes; shows a dash when partially selected.
export function SelectBox({ checked, indeterminate, onChange, testid, title = "Select for bulk action" }) {
  const partial = !!indeterminate && !checked;
  return (
    <button type="button" role="checkbox" aria-checked={checked ? "true" : partial ? "mixed" : "false"}
      title={title} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="w-5 h-5 shrink-0 inline-flex items-center justify-center cursor-pointer"
      style={{ border: "2px dashed var(--text)", borderStyle: checked || partial ? "solid" : "dashed",
        background: checked || partial ? "var(--invert-bg)" : "transparent", color: "var(--invert-text)", borderRadius: 4,
        transition: "background-color 120ms ease" }}>
      {checked ? <Check size={12} weight="bold" /> : partial ? <Minus size={12} weight="bold" /> : null}
    </button>
  );
}

// Toolbar: select-all for visible items plus bulk Archive / Unarchive of the ticked ones.
export function BulkBar({ visibleIds, sel, noun = "items", onApply, testid }) {
  const [busy, setBusy] = useState(false);
  const ids = visibleIds.filter(id => sel.selected.has(id));
  const all = visibleIds.length > 0 && ids.length === visibleIds.length;
  if (visibleIds.length === 0) return null;
  const run = async (archived) => {
    setBusy(true);
    try {
      await onApply(ids, archived);
      toast.success(`${ids.length} ${noun} ${archived ? "archived" : "restored"}`);
      sel.clear();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk update failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3 flex-wrap px-3 py-2 hair" data-testid={testid}
      style={ids.length ? { borderColor: "var(--text)" } : undefined}>
      <label className="label flex items-center gap-2 cursor-pointer">
        <SelectBox checked={all} indeterminate={ids.length > 0} testid={`${testid}-select-all`}
          title="Select all" onChange={(on) => sel.toggle(visibleIds, on)} />
        {ids.length ? <span data-testid={`${testid}-count`}>{ids.length} selected</span> : `Select all (${visibleIds.length})`}
      </label>
      {ids.length > 0 && (
        <div className="flex items-center gap-2 ml-auto">
          <button disabled={busy} onClick={() => run(true)} data-testid={`${testid}-archive`}
            className="btn-primary text-xs"><Archive size={12} weight="bold" /> Archive</button>
          <button disabled={busy} onClick={() => run(false)} data-testid={`${testid}-unarchive`}
            className="btn-ghost text-xs"><ArrowCounterClockwise size={12} weight="bold" /> Unarchive</button>
          <button onClick={sel.clear} data-testid={`${testid}-clear`} className="btn-ghost text-xs" title="Clear selection">
            <X size={12} weight="bold" /></button>
        </div>
      )}
    </div>
  );
}
